import { useState, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Button,
  StatusBadge,
  StatusTimeline,
  FileRow,
  IssueFlag,
  PDFExportButton,
  ConfirmModal,
  Skeleton,
  REPORT_PART_LABELS,
  REPORT_PART_ORDER,
} from '@rb/ui'
import type { ProjectFile, AnalysisResults, ProjectDetailData } from '@rb/ui'
import type { ProjectStatus } from '@rb/ui'
import { useFetch } from '../hooks/useFetch'
import { apiFetch } from '../utils/api'
import styles from './ProjectDetail.module.css'

const PART_DESCRIPTIONS: Record<string, string> = {
  analyse: 'Lastgang, Verbrauch und Lastspitzen im Detail',
  vorschlag: 'Empfohlene Maßnahmen zur Optimierung',
  roi: 'Wirtschaftlichkeit und Amortisation',
  finanzierung: 'Förderungen und Finanzierungsmodelle',
}

interface ProjectResponse {
  project: ProjectDetailData
  analysis_results: AnalysisResults | null
}

interface FilesResponse {
  files: ProjectFile[]
}

export default function ProjectDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const {
    data: projectData,
    loading: projectLoading,
    error: projectError,
  } = useFetch<ProjectResponse>(`/projects/${id}`)

  const {
    data: filesData,
    loading: filesLoading,
    reload: reloadFiles,
  } = useFetch<FilesResponse>(`/projects/${id}/files`)

  const [deleteFileId, setDeleteFileId] = useState<string | null>(null)
  const [deletingFile, setDeletingFile] = useState(false)
  const [uploading, setUploading] = useState(false)

  const project = projectData?.project
  const status = (project?.status ?? 'uploaded') as ProjectStatus
  const isEditable = status === 'uploaded' || status === 'action_needed'
  const hasResults = status === 'results_available'

  const ar = projectData?.analysis_results
  const partSections: Record<string, number> = {
    analyse:      ar?.analysis?.length   ?? 0,
    vorschlag:    ar?.proposal?.length   ?? 0,
    roi:          ar?.roi?.length        ?? 0,
    finanzierung: ar?.financing?.length  ?? 0,
  }

  const files = filesData?.files ?? []
  const issueFiles = files.filter(
    (f) => f.issue_code || f.quality_report?.status === 'error'
  )

  async function handleDeleteFile() {
    if (!deleteFileId || !id) return
    setDeletingFile(true)
    try {
      await apiFetch(`/projects/${id}/files/${deleteFileId}`, { method: 'DELETE' })
      setDeleteFileId(null)
      reloadFiles()
    } catch {
      // keep modal open
    } finally {
      setDeletingFile(false)
    }
  }

  async function handleReupload(fileId: string, newFile: File) {
    if (!id) return
    setUploading(true)
    try {
      await apiFetch(`/projects/${id}/files/${fileId}`, { method: 'DELETE' })
      const form = new FormData()
      form.append('files', newFile)
      await apiFetch(`/projects/${id}/files`, { method: 'POST', body: form })
      reloadFiles()
    } catch {
      // silent — user can retry
    } finally {
      setUploading(false)
    }
  }

  async function handleAddFiles(e: React.ChangeEvent<HTMLInputElement>) {
    if (!id || !e.target.files?.length) return
    setUploading(true)
    try {
      const form = new FormData()
      for (const f of Array.from(e.target.files)) {
        form.append('files', f)
      }
      await apiFetch(`/projects/${id}/files`, { method: 'POST', body: form })
      reloadFiles()
    } catch {
      // silent
    } finally {
      setUploading(false)
      e.target.value = ''
    }
  }

  if (projectLoading) {
    return (
      <div className={styles.page}>
        <Skeleton className={styles.skeletonTitle} />
        <Skeleton className={styles.skeletonTimeline} />
        <Skeleton className={styles.skeletonCard} />
      </div>
    )
  }

  if (projectError || !project) {
    return (
      <div className={styles.page}>
        <div className={styles.errorState}>
          <p>Projekt nicht gefunden.</p>
          <Button variant="secondary" onClick={() => navigate('/dashboard')}>
            Zurück
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <button className={styles.backLink} onClick={() => navigate('/dashboard')}>
        ← Meine Projekte
      </button>

      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1 className={styles.heading}>{project.name || '(Ohne Titel)'}</h1>
          <StatusBadge status={status} />
        </div>
        {hasResults && <PDFExportButton projectId={project.id} />}
      </div>

      <div className={styles.timelineWrap}>
        <StatusTimeline status={status} />
      </div>

      {status === 'action_needed' && issueFiles.length > 0 && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Aktion erforderlich</h2>
          <div className={styles.issueList}>
            {issueFiles.map((f) => (
              <IssueFlag
                key={f.id}
                code={f.issue_code ?? 'DATENFEHLER'}
                title={f.issue_title ?? f.filename}
                description={
                  f.issue_description ??
                  f.quality_report?.issues?.join('\n') ??
                  'Bitte ersetzen Sie die Datei.'
                }
                defaultOpen
              />
            ))}
          </div>
        </section>
      )}

      {hasResults && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Analyseergebnis</h2>
          <div className={styles.cardGrid}>
            {REPORT_PART_ORDER.map((part) => {
              const count = partSections[part] ?? 0
              const active = count > 0
              if (!active) {
                return (
                  <div
                    key={part}
                    className={[styles.reportCard, styles.reportCardMuted].join(' ')}
                    aria-disabled="true"
                  >
                    <span className={styles.reportCardTitle}>{REPORT_PART_LABELS[part]}</span>
                    <span className={styles.reportCardMutedNote}>noch nicht verfügbar</span>
                  </div>
                )
              }
              return (
                <button
                  key={part}
                  className={styles.reportCard}
                  onClick={() => navigate(`/projects/${project.id}/${part}`)}
                >
                  <span className={styles.reportCardTitle}>{REPORT_PART_LABELS[part]}</span>
                  <span className={styles.reportCardDesc}>{PART_DESCRIPTIONS[part]}</span>
                  <span className={styles.reportCardMeta}>
                    {count} {count === 1 ? 'Abschnitt' : 'Abschnitte'}
                    <span className={styles.reportCardArrow}>→</span>
                  </span>
                </button>
              )
            })}
          </div>
        </section>
      )}

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>
            Hochgeladene Dateien
            {!filesLoading && (
              <span className={styles.fileCount}>{files.length}</span>
            )}
          </h2>
          {isEditable && (
            <Button
              variant="secondary"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? 'Wird hochgeladen…' : '+ Datei hinzufügen'}
            </Button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className={styles.hiddenInput}
            onChange={handleAddFiles}
          />
        </div>

        {filesLoading ? (
          <div className={styles.fileList}>
            <Skeleton className={styles.skeletonRow} />
            <Skeleton className={styles.skeletonRow} />
          </div>
        ) : files.length === 0 ? (
          <div className={styles.emptyFiles}>Noch keine Dateien vorhanden.</div>
        ) : (
          <div className={styles.fileList}>
            {files.map((f) => (
              <FileRow
                key={f.id}
                file={f}
                editable={isEditable}
                onDelete={isEditable ? (fid) => setDeleteFileId(fid) : undefined}
                onReupload={isEditable ? handleReupload : undefined}
              />
            ))}
          </div>
        )}
      </section>

      {(status === 'in_review' || status === 'analysis_in_progress') && (
        <div className={styles.waitingBanner}>
          {status === 'in_review'
            ? 'Ihre Unterlagen werden geprüft. Sie erhalten eine Benachrichtigung, sobald die Analyse gestartet wird.'
            : 'Die Analyse läuft. Ergebnisse werden in Kürze verfügbar sein.'}
        </div>
      )}

      <ConfirmModal
        isOpen={!!deleteFileId}
        title="Datei löschen?"
        body="Diese Datei wird endgültig gelöscht und kann nicht wiederhergestellt werden."
        confirmLabel="Endgültig löschen"
        cancelLabel="Abbrechen"
        onConfirm={handleDeleteFile}
        onCancel={() => setDeleteFileId(null)}
        loading={deletingFile}
      />
    </div>
  )
}
